import React, { useState, useEffect, useCallback, useMemo, memo } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogContent,
  IconButton,
  useMediaQuery,
  useTheme,
  CircularProgress,
  Typography,
  Box,
  Tooltip,
} from '@mui/material';
import AuthImg from 'app/components/AuthImg';
import {
  Close as CloseIcon,
  ArrowBackIosNew as ArrowBackIosNewIcon,
  ArrowForwardIos as ArrowForwardIosIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import { formatInTimeZone } from 'date-fns-tz';
import { saveAs } from 'file-saver';
import { tokens } from 'app/components/MatxTheme/themeColors';
import { useSite } from 'app/contexts/SiteContext';

import {
  FONT_SORA,
  FONT_INTER,
  FONT_MONO,
} from 'app/views/Argyle/sites/workspace/workspaceConstants';
import { mediaPermalink } from 'app/views/Argyle/sites/workspace/permalinks';
import WorkspaceButton from 'app/views/Argyle/sites/workspace/atoms/WorkspaceButton';
import CopyLinkButton from 'app/views/Argyle/sites/workspace/atoms/CopyLinkButton';
import { coerceDate } from 'app/views/Argyle/sites/ARImageSection/arImageFields';
import { fetchImageBlob } from 'app/views/Argyle/sites/ARImageSection/arImageZip';

// Photo timestamps arrive without a zone marker, but video timestamps are
// timestamptz and already end in Z — coerceDate handles both.
const formatDate = (ts) => {
  const date = coerceDate(ts);
  return date
    ? formatInTimeZone(
        date,
        Intl.DateTimeFormat().resolvedOptions().timeZone,
        'MM/dd/yyyy HH:mm:ss',
      )
    : 'N/A';
};

const defaultEmail = (email) => email ?? 'Not a project member.';

// Blob downloads need a real filename — the auth/presigned URLs end in long
// query strings that would otherwise leak into the saved name.
const fileExt = (contentType, url, fallback) => {
  const type = String(contentType || '').toLowerCase();
  if (type.includes('jpeg') || type.includes('jpg')) return 'jpg';
  if (type.includes('png')) return 'png';
  if (type.includes('webp')) return 'webp';
  if (type.includes('heic')) return 'heic';
  if (type.includes('mp4')) return 'mp4';
  if (type.includes('quicktime')) return 'mov';
  if (type.includes('webm')) return 'webm';
  const fromUrl = String(url || '')
    .split('?')[0]
    .split('.')
    .pop();
  return fromUrl && fromUrl.length <= 5 ? fromUrl.toLowerCase() : fallback;
};

const buildDownloadName = (meta, kind, contentType, url) => {
  const date = coerceDate(meta?.timeStamp);
  const stamp = date
    ? formatInTimeZone(
        date,
        Intl.DateTimeFormat().resolvedOptions().timeZone,
        'yyyy-MM-dd_HHmmss',
      )
    : 'undated';
  return `ar-${kind}_${stamp}.${fileExt(
    contentType,
    url,
    kind === 'video' ? 'mp4' : 'jpg',
  )}`;
};

function useKeyNavigation(open, canNavigate, onPrev, onNext) {
  useEffect(() => {
    if (!open || !canNavigate) return;
    const handler = (e) => {
      if (e.key === 'ArrowLeft') onPrev();
      else if (e.key === 'ArrowRight') onNext();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, canNavigate, onPrev, onNext]);
}

const ImageSection = memo(function ImageSection({
  imageUrl,
  isLoaded,
  handlePrev,
  handleNext,
  canNavigate,
  onLoad,
}) {
  return (
    <Box
      sx={{
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        height: { xs: '50vh', sm: '60vh' },
        backgroundColor: tokens.grey[1000],
        border: `1px solid ${tokens.grey[150]}`,
        borderRadius: 2,
        overflow: 'hidden',
      }}
    >
      <IconButton
        onClick={handlePrev}
        disabled={!canNavigate}
        sx={{
          position: 'absolute',
          left: 8,
          zIndex: 2,
          backgroundColor: 'rgba(0,0,0,0.45)',
          color: tokens.grey[0],
          '&:hover': { backgroundColor: 'rgba(0,0,0,0.65)' },
          '&.Mui-disabled': { opacity: 0.3, color: tokens.grey[0] },
        }}
      >
        <ArrowBackIosNewIcon fontSize="small" />
      </IconButton>

      {!isLoaded && (
        <CircularProgress
          sx={{ position: 'absolute', zIndex: 1 }}
          color="inherit"
        />
      )}
      <AuthImg
        src={imageUrl}
        alt="Carousel"
        onLoad={onLoad}
        style={{
          maxWidth: '100%',
          maxHeight: '100%',
          objectFit: 'contain',
          opacity: isLoaded ? 1 : 0,
          transition: 'opacity 0.3s ease-in-out',
        }}
      />

      <IconButton
        onClick={handleNext}
        disabled={!canNavigate}
        sx={{
          position: 'absolute',
          right: 8,
          zIndex: 2,
          backgroundColor: 'rgba(0,0,0,0.45)',
          color: tokens.grey[0],
          '&:hover': { backgroundColor: 'rgba(0,0,0,0.65)' },
          '&.Mui-disabled': { opacity: 0.3, color: tokens.grey[0] },
        }}
      >
        <ArrowForwardIosIcon fontSize="small" />
      </IconButton>
    </Box>
  );
});

// Video counterpart of ImageSection. videoUrl/posterUrl are presigned S3 GET
// URLs, so the <video> element can load them directly — no auth pipeline.
// Keyed by clip id at the call site so navigating remounts the player and
// stops playback of the previous clip.
const VideoSection = memo(function VideoSection({
  video,
  handlePrev,
  handleNext,
  canNavigate,
}) {
  return (
    <Box
      sx={{
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        height: { xs: '50vh', sm: '60vh' },
        backgroundColor: tokens.grey[1000],
        border: `1px solid ${tokens.grey[150]}`,
        borderRadius: 2,
        overflow: 'hidden',
      }}
    >
      <IconButton
        onClick={handlePrev}
        disabled={!canNavigate}
        sx={{
          position: 'absolute',
          left: 8,
          zIndex: 2,
          backgroundColor: 'rgba(0,0,0,0.45)',
          color: tokens.grey[0],
          '&:hover': { backgroundColor: 'rgba(0,0,0,0.65)' },
          '&.Mui-disabled': { opacity: 0.3, color: tokens.grey[0] },
        }}
      >
        <ArrowBackIosNewIcon fontSize="small" />
      </IconButton>

      <video
        src={video?.videoUrl}
        poster={video?.posterUrl}
        controls
        playsInline
        preload="metadata"
        style={{
          maxWidth: '100%',
          maxHeight: '100%',
          objectFit: 'contain',
        }}
      />

      <IconButton
        onClick={handleNext}
        disabled={!canNavigate}
        sx={{
          position: 'absolute',
          right: 8,
          zIndex: 2,
          backgroundColor: 'rgba(0,0,0,0.45)',
          color: tokens.grey[0],
          '&:hover': { backgroundColor: 'rgba(0,0,0,0.65)' },
          '&.Mui-disabled': { opacity: 0.3, color: tokens.grey[0] },
        }}
      >
        <ArrowForwardIosIcon fontSize="small" />
      </IconButton>
    </Box>
  );
});

const MetaRow = ({ label, value }) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: '140px 1fr',
      gap: 1.5,
      alignItems: 'baseline',
      padding: '8px 0',
      borderBottom: `1px solid ${tokens.grey[100]}`,
      '&:last-of-type': { borderBottom: 'none' },
    }}
  >
    <Typography
      sx={{
        fontFamily: FONT_MONO,
        fontSize: 10,
        fontWeight: 600,
        letterSpacing: 0.6,
        textTransform: 'uppercase',
        color: tokens.grey[700],
      }}
    >
      {label}
    </Typography>
    <Typography
      sx={{
        fontFamily: FONT_INTER,
        fontSize: 13,
        color: tokens.grey[1000],
        wordBreak: 'break-word',
      }}
    >
      {value}
    </Typography>
  </Box>
);

const MetadataSection = memo(function MetadataSection({
  metadata,
  title = 'Photo details',
}) {
  if (!metadata) return null;
  const {
    timeStamp,
    latitude,
    longitude,
    elevation,
    deviceType,
    createdByEmail,
    sitePositionX,
    sitePositionY,
    sitePositionZ,
    procoreImageId,
    durationSeconds,
    width,
    height,
    fps,
  } = metadata;

  const hasCoords = Number.isFinite(latitude) && Number.isFinite(longitude);
  const allZero =
    (latitude ?? 0) === 0 && (longitude ?? 0) === 0 && (elevation ?? 0) === 0;
  const gps = !hasCoords
    ? 'N/A'
    : allZero
      ? 'Unknown'
      : `lat ${latitude}, lon ${longitude}, elev ${elevation ?? 0}`;

  const position = [sitePositionX, sitePositionY, sitePositionZ].every(
    Number.isFinite,
  )
    ? `X: ${sitePositionX}, Y: ${sitePositionY}, Z: ${sitePositionZ}`
    : 'N/A';

  return (
    <Box
      sx={{
        mt: 2,
        p: 2,
        backgroundColor: tokens.grey[0],
        border: `1px solid ${tokens.grey[150]}`,
        borderRadius: 2,
      }}
    >
      <Typography
        sx={{
          fontFamily: FONT_SORA,
          fontSize: 14,
          fontWeight: 600,
          color: tokens.grey[1000],
          mb: 1,
        }}
      >
        {title}
      </Typography>
      <MetaRow
        label="Timestamp"
        value={timeStamp ? formatDate(timeStamp) : 'N/A'}
      />
      <MetaRow label="GPS" value={gps} />
      <MetaRow label="Device" value={deviceType ?? 'N/A'} />
      <MetaRow label="By" value={defaultEmail(createdByEmail)} />
      <MetaRow label="Position" value={position} />
      {Number.isFinite(durationSeconds) && (
        <MetaRow
          label="Duration"
          value={`${durationSeconds.toFixed(1)} s`}
        />
      )}
      {Number.isFinite(width) && Number.isFinite(height) && (
        <MetaRow
          label="Resolution"
          value={`${width}×${height}${
            Number.isFinite(fps) ? ` @ ${fps} fps` : ''
          }`}
        />
      )}
      {procoreImageId && <MetaRow label="Procore ID" value={procoreImageId} />}
    </Box>
  );
});

export default function ImageCarouselDialog({
  open,
  onClose,
  images,
  initialIndex,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  // Every carousel host sits under /site/:siteId, which is all a media
  // permalink needs.
  const siteId = useSite()?.siteInfo?.id;

  const [idx, setIdx] = useState(initialIndex);
  const [loaded, setLoaded] = useState(false);
  const [showAR, setShowAR] = useState(true);

  useEffect(() => {
    if (open) {
      setIdx(initialIndex);
      setLoaded(false);
      setShowAR(true);
    }
  }, [open, initialIndex]);

  const current = useMemo(() => images[idx] || {}, [images, idx]);
  const video = current.video ?? null;
  const isVideo = Boolean(video);
  const meta = useMemo(() => {
    if (video) {
      // Videos store their first-frame pose as startPosition{X,Y,Z}; map it
      // onto the sitePosition fields MetadataSection already reads.
      return {
        timeStamp: video.timeStamp,
        latitude: video.latitude,
        longitude: video.longitude,
        elevation: video.elevation,
        deviceType: video.deviceType,
        createdByEmail: video.createdByEmail,
        sitePositionX: video.startPositionX,
        sitePositionY: video.startPositionY,
        sitePositionZ: video.startPositionZ,
        durationSeconds: video.durationSeconds,
        width: video.width,
        height: video.height,
        fps: video.fps,
      };
    }
    return current.image?.metadata ?? current.image ?? null;
  }, [current, video]);
  const canNavigate = images.length > 1;

  const imageUrl = useMemo(
    () =>
      isVideo
        ? null
        : showAR
          ? current.image?.augmentedUrl
          : current.image?.worldUrl,
    [showAR, current, isVideo],
  );

  useEffect(() => setLoaded(false), [imageUrl]);

  const prev = useCallback(() => {
    if (!canNavigate) return;
    setIdx((i) => (i === 0 ? images.length - 1 : i - 1));
    setShowAR(true);
  }, [canNavigate, images.length]);

  const next = useCallback(() => {
    if (!canNavigate) return;
    setIdx((i) => (i === images.length - 1 ? 0 : i + 1));
    setShowAR(true);
  }, [canNavigate, images.length]);

  const downloadUrl = isVideo ? video?.videoUrl : imageUrl;
  const mediaId = isVideo
    ? video?.id
    : (current.image?.id ?? current.image?.Id);

  // Save through a Blob: the `download` attribute is ignored on cross-origin
  // URLs (mobile browsers just navigate to the file), and photo URLs are
  // auth-protected so a bare link wouldn't load at all.
  const [downloading, setDownloading] = useState(false);
  const download = useCallback(async () => {
    if (!downloadUrl || downloading) return;
    setDownloading(true);
    try {
      let blob;
      let contentType;
      if (isVideo) {
        // videoUrl is a presigned S3 GET URL — carries its own signature.
        const res = await fetch(downloadUrl);
        if (!res.ok) throw new Error(`Video fetch failed (${res.status})`);
        blob = await res.blob();
        contentType = blob.type;
      } else {
        ({ blob, contentType } = await fetchImageBlob(downloadUrl));
      }
      saveAs(
        blob,
        buildDownloadName(
          meta,
          isVideo ? 'video' : 'photo',
          contentType,
          downloadUrl,
        ),
      );
    } catch {
      // Blob fetch failed (e.g. CORS) — fall back to opening the file.
      window.open(downloadUrl, '_blank', 'noopener');
    } finally {
      setDownloading(false);
    }
  }, [downloadUrl, downloading, isVideo, meta]);

  const toggleView = useCallback(() => setShowAR((v) => !v), []);

  useKeyNavigation(open, canNavigate, prev, next);

  if (!open || !images.length) return null;

  return (
    <Dialog
      open
      fullWidth
      maxWidth="md"
      fullScreen={isMobile}
      onClose={onClose}
      PaperProps={{
        sx: {
          backgroundColor: tokens.grey[50],
          borderRadius: isMobile ? 0 : 2,
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '14px 20px',
          borderBottom: `1px solid ${tokens.grey[150]}`,
          gap: 1,
        }}
      >
        <Typography
          sx={{
            fontFamily: FONT_SORA,
            fontSize: 16,
            fontWeight: 600,
            color: tokens.grey[1000],
          }}
        >
          {meta?.timeStamp ? formatDate(meta.timeStamp) : 'Media'}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {/* AR/World toggle only applies to photos — clips have one file. */}
          {!isVideo && (
            <WorkspaceButton variant="ghost" onClick={toggleView}>
              {showAR ? 'AR view' : 'World view'}
            </WorkspaceButton>
          )}
          {siteId != null && mediaId != null && (
            <CopyLinkButton path={mediaPermalink(siteId, mediaId, isVideo)} />
          )}
          <Tooltip title={downloading ? 'Downloading…' : 'Download'}>
            {/* span keeps the tooltip working while the button is disabled */}
            <span>
              <IconButton
                size="small"
                onClick={download}
                disabled={downloading || !downloadUrl}
              >
                {downloading ? (
                  <CircularProgress size={18} thickness={5} />
                ) : (
                  <DownloadIcon
                    sx={{
                      fontSize: 18,
                      color: tokens.grey[800],
                    }}
                  />
                )}
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Close">
            <IconButton size="small" onClick={onClose}>
              <CloseIcon
                sx={{
                  fontSize: 18,
                  color: tokens.grey[800],
                }}
              />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <DialogContent sx={{ p: { xs: 2, sm: 3 } }}>
        {isVideo ? (
          <VideoSection
            key={video.id}
            video={video}
            handlePrev={prev}
            handleNext={next}
            canNavigate={canNavigate}
          />
        ) : (
          <ImageSection
            imageUrl={imageUrl}
            isLoaded={loaded}
            onLoad={() => setLoaded(true)}
            handlePrev={prev}
            handleNext={next}
            canNavigate={canNavigate}
          />
        )}
        <MetadataSection
          metadata={meta}
          title={isVideo ? 'Video details' : 'Photo details'}
        />
      </DialogContent>
    </Dialog>
  );
}

ImageCarouselDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  // Each item is either { image } (AR photo) or { video } (AR video clip).
  images: PropTypes.arrayOf(
    PropTypes.shape({
      image: PropTypes.object,
      video: PropTypes.object,
    }),
  ).isRequired,
  initialIndex: PropTypes.number,
};
